import { useEffect, useState } from "react";
import { Link } from "react-router";
import type { MessageKey } from "@wato/i18n";
import { useI18n } from "@wato/i18n/react";
import { Collapsible, HelpPopover, Stack, buttonVariantClasses, cx } from "@wato/ui";
import { useApi } from "../../api/context";
import { mayUse } from "../roles/role-label";
import { useWorkspace } from "../workspace/WorkspaceContext";

type StepId = "whatsapp" | "template" | "flow" | "catalog";
type Known = boolean | null;

interface GetStartedStep {
  readonly id: StepId;
  readonly to: string;
  readonly done: Known;
}

/** b106: the four required steps (done state is real data) plus QR AI as an optional path. */
export function GetStartedCard({ workspaceId }: { workspaceId: string }) {
  const { t } = useI18n();
  const api = useApi();
  const { workspace } = useWorkspace();
  const [done, setDone] = useState<Record<StepId, Known>>({
    whatsapp: null,
    template: null,
    flow: null,
    catalog: null,
  });
  const canTemplates = mayUse(workspace, "templates.view");
  const canFlows = mayUse(workspace, "flows.view");
  const canCommerce = mayUse(workspace, "commerce.view");

  useEffect(() => {
    let live = true;
    const run = <T,>(on: boolean, f: () => Promise<T>, pick: (v: T) => boolean) =>
      on ? f().then(pick, () => null) : Promise.resolve<Known>(null);
    void Promise.all([
      run(
        mayUse(workspace, "channels.view"),
        () => api.listChannels(workspaceId),
        (c) => c.some((x) => x.provider === "whatsapp_cloud" && x.status === "connected"),
      ),
      run(
        canTemplates,
        () => api.listTemplates(workspaceId),
        (l) => l.some((x) => x.status === "approved"),
      ),
      run(
        canFlows,
        () => api.listFlows(workspaceId),
        (l) => l.some((x) => x.status === "published"),
      ),
      run(
        canCommerce,
        () => api.listCartProducts(workspaceId, {}),
        (l) => l.length > 0,
      ),
    ]).then(([whatsapp, template, flow, catalog]) => {
      if (live) setDone({ whatsapp, template, flow, catalog });
    });
    return () => {
      live = false;
    };
  }, [api, workspace, workspaceId, canTemplates, canFlows, canCommerce]);

  const steps: GetStartedStep[] = [
    { id: "whatsapp", to: "channels", done: done.whatsapp },
    { id: "template", to: "templates", done: done.template },
    { id: "flow", to: "flows", done: done.flow },
    { id: "catalog", to: "store", done: done.catalog },
  ];
  const shown = steps.filter((s) => s.done !== null);
  if (shown.length === 0 || shown.every((s) => s.done)) return null;
  const count = shown.filter((s) => s.done).length;
  const key = (id: StepId, part: "title" | "body" | "cta") =>
    `dashboard.getStarted.${id}.${part}` as MessageKey;
  // G1: the filled primary is the next-step card's; here every step is a quiet secondary link.
  const link = cx(
    "inline-flex h-9 items-center rounded-md px-3 text-sm font-medium",
    buttonVariantClasses.secondary,
  );

  return (
    <section
      aria-label={t("dashboard.getStarted.title")}
      className="rounded-2xl border border-app-line bg-app-surface p-5"
      data-testid="get-started"
    >
      <Collapsible
        title={
          <h2 className="m-0 text-lg font-semibold text-fg">{t("dashboard.getStarted.title")}</h2>
        }
        summary={t("dashboard.getStarted.progress", { done: count, total: shown.length })}
        testId="get-started-steps"
      >
        <Stack as="ol" gap={2}>
          {shown.map((s) => (
            <li
              key={s.id}
              data-step={s.id}
              data-done={s.done ? "true" : "false"}
              className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-line p-3"
            >
              <div className="flex items-center gap-1">
                <p className="m-0 text-sm font-medium text-fg">
                  <span aria-hidden="true">{s.done ? "✓ " : "○ "}</span>
                  {t(key(s.id, "title"))}
                </p>
                <HelpPopover testId={`get-started-help-${s.id}`}>
                  <p className="m-0">{t(key(s.id, "body"))}</p>
                </HelpPopover>
              </div>
              {s.done ? (
                <span className="text-xs text-fg-muted">{t("dashboard.getStarted.done")}</span>
              ) : (
                <Link to={`/w/${workspaceId}/${s.to}`} className={link}>
                  {t(key(s.id, "cta"))}
                </Link>
              )}
            </li>
          ))}
          {mayUse(workspace, "channels.view") ? (
            <li
              data-step="qrAi"
              className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-dashed border-line p-3"
            >
              <div className="flex items-center gap-1">
                <p className="m-0 text-sm font-medium text-fg">
                  {t("dashboard.getStarted.qrAi.title")}
                </p>
                <HelpPopover testId="get-started-help-qrAi">
                  <p className="m-0">{t("dashboard.getStarted.qrAi.body")}</p>
                </HelpPopover>
              </div>
              <Link
                to={`/w/${workspaceId}/channels`}
                className={cx(
                  "inline-flex h-9 items-center rounded-md px-3 text-sm font-medium",
                  buttonVariantClasses.secondary,
                )}
              >
                {t("dashboard.getStarted.qrAi.cta")}
              </Link>
            </li>
          ) : null}
        </Stack>
      </Collapsible>
    </section>
  );
}
